import { Icon, Modal } from "materia"
import { useCallback, useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { modifiers } from "ui-tools"
import type { AttachmentRef } from "../../types"
import { fitImage } from "./fitImage"
import s from "./AttachmentImage.module.scss"

/** The view URL for an Attachment, or null when it is gone or not ours to see. */
export type ResolveAttachmentUrl = (
  id: string,
  opts?: { fresh?: boolean }
) => Promise<string | null>

type AttachmentImageProps = {
  attachment: AttachmentRef
  /** Ours sits on the right. */
  isOwn: boolean
  resolveUrl: ResolveAttachmentUrl
}

type ImageState =
  | { status: "loading" }
  | { status: "shown"; url: string }
  | { status: "gone" }
  | { status: "failed" }

/**
 * An image in the thread. Its box is sized from the message's width and height
 * before anything loads, so the thread never jumps. A URL that stops working
 * (expired, or the cache was stale) is asked for once more before giving up.
 */
export function AttachmentImage({
  attachment,
  isOwn,
  resolveUrl,
}: AttachmentImageProps) {
  const { t } = useTranslation()
  const [state, setState] = useState<ImageState>({ status: "loading" })
  const [isOpen, setIsOpen] = useState(false)
  const retried = useRef(false)
  // The URL whose failure asked for the fresh one; the thread and modal <img>s
  // share a URL, so both report its failure.
  const refreshedFrom = useRef<string>()
  const { id, width, height } = attachment

  const load = useCallback(
    (fresh: boolean) => {
      let cancelled = false
      resolveUrl(id, { fresh })
        .then((url) => {
          if (!cancelled)
            setState(url ? { status: "shown", url } : { status: "gone" })
        })
        .catch(() => {
          if (!cancelled) setState({ status: "failed" })
        })
      return () => {
        cancelled = true
      }
    },
    [id, resolveUrl]
  )

  useEffect(() => load(false), [load])

  // One fresh URL per failure: a URL that loads earns the next expiry its own.
  function onImageLoad() {
    retried.current = false
  }

  // Keeps showing (and any open modal) until the fresh URL arrives.
  function onImageError(url: string) {
    if (url === refreshedFrom.current) return
    if (retried.current) return setState({ status: "failed" })
    retried.current = true
    refreshedFrom.current = url
    load(true)
  }

  function retry() {
    retried.current = false
    setState({ status: "loading" })
    load(true)
  }

  const box = {
    width: fitImage(width, height),
    aspectRatio: `${width} / ${height}`,
  }

  if (state.status === "gone" || state.status === "failed") {
    const isGone = state.status === "gone"
    const icon = isGone ? "Attachment" : "ExclamationMarkCircle"
    const note = isGone ? t("chat.IMAGE_GONE") : t("chat.IMAGE_FAILED")

    return (
      <div
        className={modifiers(s, "image", "placeholder", { isOwn })}
        style={box}
      >
        <Icon icon={icon} size="large" />
        <p className={s.image__note}>{note}</p>
        {!isGone && (
          <button type="button" className={s.image__retry} onClick={retry}>
            {t("chat.MESSAGE_RETRY")}
          </button>
        )}
      </div>
    )
  }

  if (state.status === "loading") {
    return (
      <div
        className={modifiers(s, "image", "loading", { isOwn })}
        style={box}
        role="img"
        aria-busy="true"
        aria-label={String(t("chat.IMAGE_ALT"))}
      />
    )
  }

  return (
    <>
      <button
        type="button"
        className={modifiers(s, "image", { isOwn })}
        style={box}
        onClick={() => setIsOpen(true)}
        aria-label={String(t("chat.IMAGE_OPEN_ARIA"))}
      >
        <img
          className={s.image__img}
          src={state.url}
          alt={String(t("chat.IMAGE_ALT"))}
          onLoad={onImageLoad}
          onError={() => onImageError(state.url)}
        />
      </button>

      {isOpen && (
        <Modal
          ariaLabel={String(t("chat.IMAGE_ALT"))}
          onClose={() => setIsOpen(false)}
          showCloseButton
          isFullScreenMobile
        >
          <img
            className={s.image__full}
            src={state.url}
            alt={String(t("chat.IMAGE_ALT"))}
            onLoad={onImageLoad}
            onError={() => onImageError(state.url)}
          />
        </Modal>
      )}
    </>
  )
}
